/** @module @liquidefy/code-editor/language/language-diagnostics @version 1.0.0 @license LQD Research LLC */

// ═══ §1 IMPORTS — types and engine dependencies ═══

import type {
  DiagnosticInfo,
  DiagnosticSeverity,
  DiagnosticTag,
  DiagnosticId,
  CodeAction,
  CodeActionId,
  CodeActionKind,
  EditOperation,
  MarkerState,
  MarkerKind,
  MarkerId,
  Range,
  Position,
  LanguageId,
  EditorResult,
} from '../code-editor-types.js';

import {
  ok,
  err,
  asCodeActionId,
  asDiagnosticId,
  asMarkerId,
  generateId,
  rangesOverlap,
  rangeContains,
  comparePositions,
  positionToOffset,
  offsetToPosition,
} from '../code-editor.js';

// ═══ §2 DIAGNOSTIC MANAGEMENT — CRUD and edit-aware updates ═══

export interface IdentifiedDiagnostic extends DiagnosticInfo {
  readonly id: DiagnosticId;
  readonly documentVersion?: number;
}

/**
 * Add a diagnostic to the collection. Assigns an id if not already present.
 */
export function addDiagnostic(
  diagnostics: readonly IdentifiedDiagnostic[],
  diagnostic: IdentifiedDiagnostic,
): IdentifiedDiagnostic[] {
  return [...diagnostics, diagnostic];
}

/**
 * Remove a diagnostic by its id.
 */
export function removeDiagnostic(
  diagnostics: readonly IdentifiedDiagnostic[],
  id: DiagnosticId,
): IdentifiedDiagnostic[] {
  return diagnostics.filter((d) => d.id !== id);
}

/**
 * Clear all diagnostics, optionally filtering by source.
 */
export function clearDiagnostics(
  diagnostics: readonly IdentifiedDiagnostic[],
  source?: string,
): IdentifiedDiagnostic[] {
  if (source === undefined) return [];
  return diagnostics.filter((d) => d.source !== source);
}

/**
 * Shift diagnostic ranges after an edit operation.
 * Diagnostics that overlap the edit range are removed.
 * Diagnostics after the edit are shifted accordingly.
 */
export function updateDiagnosticsForEdit(
  diagnostics: readonly IdentifiedDiagnostic[],
  edit: EditOperation,
): IdentifiedDiagnostic[] {
  const result: IdentifiedDiagnostic[] = [];
  const editStartLine = edit.range.start.line;
  const editEndLine = edit.range.end.line;
  const insertedLines = edit.text.split('\n').length - 1;
  const deletedLines = editEndLine - editStartLine;
  const lineDelta = insertedLines - deletedLines;

  for (const diag of diagnostics) {
    // Diagnostic is entirely before the edit — keep as-is
    if (comparePositions(diag.range.end, edit.range.start) <= 0) {
      result.push(diag);
      continue;
    }

    // Diagnostic overlaps the edit range — remove it
    if (rangesOverlap(diag.range, edit.range)) {
      continue;
    }

    // Diagnostic is entirely after the edit — shift lines
    if (comparePositions(diag.range.start, edit.range.end) >= 0) {
      result.push({
        ...diag,
        range: {
          start: { line: diag.range.start.line + lineDelta, column: diag.range.start.column },
          end: { line: diag.range.end.line + lineDelta, column: diag.range.end.column },
        },
      });
    }
  }

  return result;
}

/**
 * Merge incoming diagnostics from a specific source with existing diagnostics.
 * Replaces all diagnostics from that source.
 */
export function mergeDiagnostics(
  existing: readonly IdentifiedDiagnostic[],
  incoming: readonly IdentifiedDiagnostic[],
  source: string,
): IdentifiedDiagnostic[] {
  const filtered = existing.filter((d) => d.source !== source);
  return [...filtered, ...incoming];
}

// ═══ §3 DIAGNOSTIC FILTERING — severity, source, range, tags, grouping ═══

/**
 * Filter diagnostics by severity level.
 */
export function filterBySeverity(
  diagnostics: readonly IdentifiedDiagnostic[],
  severity: DiagnosticSeverity,
): IdentifiedDiagnostic[] {
  return diagnostics.filter((d) => d.severity === severity);
}

/**
 * Filter diagnostics by source identifier.
 */
export function filterBySource(
  diagnostics: readonly IdentifiedDiagnostic[],
  source: string,
): IdentifiedDiagnostic[] {
  return diagnostics.filter((d) => d.source === source);
}

/**
 * Filter diagnostics whose range overlaps the given range.
 */
export function filterByRange(
  diagnostics: readonly IdentifiedDiagnostic[],
  range: Range,
): IdentifiedDiagnostic[] {
  return diagnostics.filter((d) => rangesOverlap(d.range, range));
}

/**
 * Filter diagnostics that have any of the specified tags.
 */
export function filterByTags(
  diagnostics: readonly IdentifiedDiagnostic[],
  tags: readonly DiagnosticTag[],
): IdentifiedDiagnostic[] {
  return diagnostics.filter((d) =>
    d.tags !== undefined && d.tags.some((t) => tags.includes(t)),
  );
}

/**
 * Group diagnostics by severity level.
 */
export function groupBySeverity(
  diagnostics: readonly IdentifiedDiagnostic[],
): Record<DiagnosticSeverity, IdentifiedDiagnostic[]> {
  const groups: Record<DiagnosticSeverity, IdentifiedDiagnostic[]> = {
    error: [],
    warning: [],
    info: [],
    hint: [],
  };
  for (const d of diagnostics) {
    groups[d.severity].push(d);
  }
  return groups;
}

/**
 * Group diagnostics by source.
 */
export function groupBySource(
  diagnostics: readonly IdentifiedDiagnostic[],
): Record<string, IdentifiedDiagnostic[]> {
  const groups: Record<string, IdentifiedDiagnostic[]> = {};
  for (const d of diagnostics) {
    const src = d.source ?? 'unknown';
    if (groups[src] === undefined) {
      groups[src] = [];
    }
    groups[src].push(d);
  }
  return groups;
}

/**
 * Group diagnostics by file URI.
 */
export function groupByFile(
  diagnostics: readonly IdentifiedDiagnostic[],
): Record<string, IdentifiedDiagnostic[]> {
  const groups: Record<string, IdentifiedDiagnostic[]> = {};
  for (const d of diagnostics) {
    const info = d.relatedInformation;
    const uri = info !== undefined && info.length > 0 ? info[0]!.uri : 'current';
    if (groups[uri] === undefined) {
      groups[uri] = [];
    }
    groups[uri].push(d);
  }
  return groups;
}

type SortBy = 'severity' | 'position' | 'source';

/**
 * Sort diagnostics by severity, position, or source.
 */
export function sortDiagnostics(
  diagnostics: readonly IdentifiedDiagnostic[],
  by: SortBy,
): IdentifiedDiagnostic[] {
  const sorted = [...diagnostics];
  switch (by) {
    case 'severity':
      sorted.sort((a, b) => severityToNumber(a.severity) - severityToNumber(b.severity));
      break;
    case 'position':
      sorted.sort((a, b) => comparePositions(a.range.start, b.range.start));
      break;
    case 'source':
      sorted.sort((a, b) => (a.source ?? '').localeCompare(b.source ?? ''));
      break;
  }
  return sorted;
}

// ═══ §4 DIAGNOSTIC ANALYSIS — summaries and queries ═══

export interface DiagnosticSummaryResult {
  readonly errors: number;
  readonly warnings: number;
  readonly infos: number;
  readonly hints: number;
  readonly total: number;
}

/**
 * Produce a summary of diagnostic counts by severity.
 */
export function diagnosticSummary(
  diagnostics: readonly IdentifiedDiagnostic[],
): DiagnosticSummaryResult {
  let errors = 0;
  let warnings = 0;
  let infos = 0;
  let hints = 0;

  for (const d of diagnostics) {
    switch (d.severity) {
      case 'error': errors++; break;
      case 'warning': warnings++; break;
      case 'info': infos++; break;
      case 'hint': hints++; break;
    }
  }

  return { errors, warnings, infos, hints, total: diagnostics.length };
}

/**
 * Check whether any diagnostic has error severity.
 */
export function hasErrors(diagnostics: readonly IdentifiedDiagnostic[]): boolean {
  return diagnostics.some((d) => d.severity === 'error');
}

/**
 * Check whether any diagnostic has warning severity.
 */
export function hasWarnings(diagnostics: readonly IdentifiedDiagnostic[]): boolean {
  return diagnostics.some((d) => d.severity === 'warning');
}

/**
 * Find the diagnostic with the highest severity. Error > Warning > Info > Hint.
 */
export function mostSevere(
  diagnostics: readonly IdentifiedDiagnostic[],
): IdentifiedDiagnostic | null {
  if (diagnostics.length === 0) return null;

  let best = diagnostics[0]!;
  let bestNum = severityToNumber(best.severity);

  for (let i = 1; i < diagnostics.length; i++) {
    const num = severityToNumber(diagnostics[i]!.severity);
    if (num < bestNum) {
      best = diagnostics[i]!;
      bestNum = num;
    }
  }

  return best ?? null;
}

/**
 * Find the diagnostic at a specific position.
 */
export function diagnosticAtPosition(
  diagnostics: readonly IdentifiedDiagnostic[],
  position: Position,
): IdentifiedDiagnostic | null {
  for (const d of diagnostics) {
    if (rangeContains(d.range, position)) {
      return d;
    }
  }
  return null;
}

/**
 * Find all diagnostics whose range overlaps the given range.
 */
export function diagnosticsInRange(
  diagnostics: readonly IdentifiedDiagnostic[],
  range: Range,
): IdentifiedDiagnostic[] {
  return diagnostics.filter((d) => rangesOverlap(d.range, range));
}

/**
 * Check whether a diagnostic is stale relative to the current document version.
 */
export function isDiagnosticStale(
  diagnostic: IdentifiedDiagnostic,
  documentVersion: number,
): boolean {
  if (diagnostic.documentVersion === undefined) return false;
  return diagnostic.documentVersion < documentVersion;
}

// ═══ §5 CODE ACTIONS — quick fixes and refactorings ═══

/**
 * Get available code actions for a diagnostic.
 * Generates quick fixes based on common diagnostic patterns.
 */
export function getCodeActionsForDiagnostic(
  diagnostic: IdentifiedDiagnostic,
): CodeAction[] {
  const actions: CodeAction[] = [];
  const msg = diagnostic.message.toLowerCase();

  if (diagnostic.severity === 'error') {
    if (msg.includes('missing') || msg.includes('expected')) {
      actions.push({
        id: asCodeActionId(generateId('fix')),
        title: 'Fix: Add missing element',
        kind: 'quickfix',
        diagnostics: [diagnostic],
        isPreferred: true,
      });
    }
    if (msg.includes('unused') || msg.includes('not used')) {
      actions.push({
        id: asCodeActionId(generateId('fix')),
        title: 'Remove unused declaration',
        kind: 'quickfix',
        diagnostics: [diagnostic],
        edit: [{
          kind: 'delete',
          range: diagnostic.range,
          text: '',
          origin: 'auto-format',
        }],
        isPreferred: true,
      });
    }
    if (msg.includes('import')) {
      actions.push({
        id: asCodeActionId(generateId('fix')),
        title: 'Add missing import',
        kind: 'quickfix',
        diagnostics: [diagnostic],
        isPreferred: true,
      });
    }
  }

  if (diagnostic.tags?.includes('deprecated')) {
    actions.push({
      id: asCodeActionId(generateId('fix')),
      title: 'Replace deprecated usage',
      kind: 'quickfix',
      diagnostics: [diagnostic],
    });
  }

  if (diagnostic.tags?.includes('unnecessary')) {
    actions.push({
      id: asCodeActionId(generateId('fix')),
      title: 'Remove unnecessary code',
      kind: 'quickfix',
      diagnostics: [diagnostic],
      edit: [{
        kind: 'delete',
        range: diagnostic.range,
        text: '',
        origin: 'auto-format',
      }],
      isPreferred: true,
    });
  }

  return actions;
}

/**
 * Apply a code action to content, returning the modified content.
 */
export function applyCodeAction(content: string, action: CodeAction): EditorResult<string> {
  if (action.edit === undefined || action.edit.length === 0) {
    return ok(content, 'NO_EDIT', 'Code action has no edits');
  }

  let result = content;
  const sortedEdits = [...action.edit].sort((a, b) =>
    comparePositions(b.range.start, a.range.start),
  );

  for (const edit of sortedEdits) {
    const start = positionToOffset(result, edit.range.start);
    const end = positionToOffset(result, edit.range.end);
    if (start < 0 || end < 0 || start > result.length || end > result.length) {
      return err('INVALID_RANGE', `Edit range out of bounds: ${start}-${end}`);
    }
    result = result.slice(0, start) + edit.text + result.slice(end);
  }

  return ok(result, 'ACTION_APPLIED', `Applied code action: ${action.title}`);
}

/**
 * Get quick fixes for a diagnostic (convenience alias for getCodeActionsForDiagnostic
 * filtered to quickfix kind).
 */
export function getQuickFixes(diagnostic: IdentifiedDiagnostic): CodeAction[] {
  return getCodeActionsForDiagnostic(diagnostic).filter((a) => a.kind === 'quickfix');
}

/**
 * Get refactoring actions for a content range and language.
 */
export function getRefactorings(
  content: string,
  range: Range,
  _language: LanguageId,
): CodeAction[] {
  const actions: CodeAction[] = [];
  const start = positionToOffset(content, range.start);
  const end = positionToOffset(content, range.end);
  const selected = content.slice(start, end);

  if (selected.length > 0) {
    actions.push({
      id: asCodeActionId(generateId('refactor')),
      title: 'Extract to variable',
      kind: 'refactor.extract',
    });
    actions.push({
      id: asCodeActionId(generateId('refactor')),
      title: 'Extract to function',
      kind: 'refactor.extract',
    });
    if (selected.includes('=>') || selected.includes('function')) {
      actions.push({
        id: asCodeActionId(generateId('refactor')),
        title: 'Inline function',
        kind: 'refactor.inline',
      });
    }
  }

  return actions;
}

/**
 * Generate an "organize imports" code action for the given content.
 */
export function organizeImports(content: string, _language: LanguageId): CodeAction {
  return {
    id: asCodeActionId(generateId('organize')),
    title: 'Organize Imports',
    kind: 'source.organizeImports',
    isPreferred: true,
  };
}

/**
 * Generate a "sort members" code action for the given content.
 */
export function sortMembers(content: string, _language: LanguageId): CodeAction {
  return {
    id: asCodeActionId(generateId('sort')),
    title: 'Sort Members',
    kind: 'source',
  };
}

// ═══ §6 DIAGNOSTIC DISPLAY — formatting and rendering helpers ═══

/**
 * Format a diagnostic into a human-readable string.
 */
export function formatDiagnostic(diagnostic: IdentifiedDiagnostic): string {
  const loc = `${diagnostic.range.start.line + 1}:${diagnostic.range.start.column + 1}`;
  const severity = diagnostic.severity.toUpperCase();
  const source = diagnostic.source !== undefined ? `[${diagnostic.source}]` : '';
  const code = diagnostic.code !== undefined ? ` (${diagnostic.code})` : '';
  return `${loc} ${severity}${code}${source}: ${diagnostic.message}`;
}

/**
 * Convert a diagnostic to a MarkerState for gutter display.
 */
export function diagnosticToMarker(diagnostic: IdentifiedDiagnostic): MarkerState {
  return {
    id: asMarkerId(diagnostic.id as string),
    kind: 'diagnostic' as MarkerKind,
    range: diagnostic.range,
    data: {
      severity: diagnostic.severity,
      source: diagnostic.source ?? '',
      code: diagnostic.code ?? '',
      message: diagnostic.message,
      relatedInfo: diagnostic.relatedInformation?.map((ri) => ({
        uri: ri.uri,
        range: ri.range,
        message: ri.message,
      })) ?? [],
      quickFixes: [],
    },
    stickiness: 'grows-only-when-typing-before',
  };
}

/**
 * Map severity to a display icon character.
 */
export function severityToIcon(severity: DiagnosticSeverity): string {
  switch (severity) {
    case 'error': return 'x';
    case 'warning': return '!';
    case 'info': return 'i';
    case 'hint': return '*';
  }
}

/**
 * Map severity to a CSS color string.
 */
export function severityToColor(severity: DiagnosticSeverity): string {
  switch (severity) {
    case 'error': return '#f44747';
    case 'warning': return '#cca700';
    case 'info': return '#3794ff';
    case 'hint': return '#6a9955';
  }
}

/**
 * Build rich tooltip content for a diagnostic.
 */
export function diagnosticToTooltip(diagnostic: IdentifiedDiagnostic): string {
  const lines: string[] = [];
  const icon = severityToIcon(diagnostic.severity);
  const source = diagnostic.source !== undefined ? ` [${diagnostic.source}]` : '';
  const code = diagnostic.code !== undefined ? ` (${diagnostic.code})` : '';

  lines.push(`${icon} ${diagnostic.severity}${code}${source}`);
  lines.push(diagnostic.message);

  if (diagnostic.relatedInformation) {
    for (const related of diagnostic.relatedInformation) {
      lines.push(`  -> ${related.uri}:${related.range.start.line + 1}: ${related.message}`);
    }
  }

  if (diagnostic.tags) {
    if (diagnostic.tags.includes('deprecated')) {
      lines.push('  [deprecated]');
    }
    if (diagnostic.tags.includes('unnecessary')) {
      lines.push('  [unnecessary]');
    }
  }

  return lines.join('\n');
}

// ═══ INTERNAL HELPERS ═══

function severityToNumber(severity: DiagnosticSeverity): number {
  switch (severity) {
    case 'error': return 0;
    case 'warning': return 1;
    case 'info': return 2;
    case 'hint': return 3;
  }
}
